import React, { useMemo, useState } from 'react';
import {
    Booking,
    BookingStatus,
    EventStatuses,
    ProductType,
    ResourceType,
    SourceType,
    TransactionType,
    useAddBookingTransaction,
    useCancelBooking,
    useHoldBookingAvailability,
    useReserveBooking,
} from '@travelity/api';
import { Box, Divider, Stack } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { format } from 'date-fns';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { durationToHumanReadable, formatMoney } from '@travelity/utils';
import {
    Card,
    ConfirmationDialog,
    IconButton,
    IconButtonGroup,
    SquareCard,
    StatusTag,
    StatusTagStatuses,
    Tag,
} from '@travelity/ui';
import { formatValue } from '@travelity/form';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { useToast } from '../../hooks';
import PerfectScrollbar from 'react-perfect-scrollbar';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { TransactionModal } from './components/transaction-modal';
import { ReasonModal } from './components/reason-modal';
import BookingItemSkeleton from './components/booking-item-skeleton';
import { OverlayWithReason } from '../overlay-with-reason';
import HoldAvailabilityDialog from './components/hold-availability-dialog';
import { MoveBookingModal } from '../move-booking-modal';
import { useResourcePermissions, useSuperUser } from '../../contexts/user';

export interface BookingItemProps {
    booking: Booking;
    isSelected: boolean;
    refetch: () => void;
}

const BookingItem: React.FC<BookingItemProps> = ({
    booking,
    refetch,
    isSelected,
}) => {
    const { t: pt } = useTranslation('product');
    const { t } = useTranslation();
    const { showError, showSuccess } = useToast();
    const {
        id,
        status,
        reason,
        orderId,
        date,
        source,

        participants,
        financials,
        product,
        shared,
    } = booking;

    const { pricing } = product;

    const [moveBooking, setMoveBooking] = useState(false);

    const [deletingBooking, setDeletingBooking] = useState<string>();

    const [payDraft, setPayDraft] = useState(false);
    const [holdBooking, setHoldBooking] = useState(false);
    const { mutate: holdAvailability, isLoading: isHoldBookingLoading } =
        useHoldBookingAvailability({
            onSuccess: () => {
                showSuccess('HOLD_BOOKING_SUCCESS');
                setHoldBooking(false);
                refetch();
            },
            onError: () => {
                showError('HOLD_BOOKING_FAILURE');
            },
        });
    const { mutate: reserve, isLoading: isReserveLoading } = useReserveBooking({
        onSuccess: () => {
            showSuccess('RESERVE_BOOKING_SUCCESS');
            setHoldBooking(false);
            refetch();
        },
        onError: () => {
            showError('RESERVE_BOOKING_FAILURE');
        },
    });
    const [payBooking, setPayBooking] = useState(false);
    const { mutate: addTransaction, isLoading: isAddTransactionLoading } =
        useAddBookingTransaction({
            onSuccess: (data, transaction) => {
                setPayBooking(false);
                refetch();
                showSuccess(
                    transaction.type === TransactionType.PAYMENT
                        ? 'ADD_PAYMENT_SUCCESS'
                        : transaction.type === TransactionType.REFUND
                        ? 'ADD_REFUND_SUCCESS'
                        : 'ADD_WAIVER_SUCCESS',
                    {
                        amount: transaction.amount,
                        currency: pricing.currency.abbr,
                    }
                );
            },
            onError: (err, transaction) => {
                showError(
                    transaction.type === TransactionType.PAYMENT
                        ? 'ADD_PAYMENT_FAILURE'
                        : transaction.type === TransactionType.REFUND
                        ? 'ADD_REFUND_FAILURE'
                        : 'ADD_WAIVER_FAILURE',
                    {
                        amount: transaction.amount,
                        currency: pricing.currency.abbr,
                    }
                );
            },
        });
    const { mutate: removeBooking, isLoading: isRemoveBookingLoading } =
        useCancelBooking({
            onSuccess: () => {
                setDeletingBooking(undefined);
                refetch();
                showSuccess('CANCEL_BOOKING_SUCCESS', {
                    productName: product.name,
                });
            },
            onError: () => {
                showError('CANCEL_BOOKING_FAILURE', {
                    productName: product.name,
                });
            },
        });

    const duration = date.duration
        ? durationToHumanReadable(date.duration)
        : '';

    const discountFormatted =
        pricing.discount?.type === 'absolute'
            ? formatMoney(pricing.discount?.amount, pricing.currency.abbr)
            : `${pricing.discount?.amount}%`;

    const finalPrice =
        pricing && financials
            ? formatMoney(financials.total, pricing.currency.abbr)
            : '';

    const sourceValue = useMemo(() => {
        // @ts-ignore
        return source.type === SourceType.DIRECT
            ? // @ts-ignore
              t(source.name, { ns: 'team' })
            : source.name;
    }, [source]);
    const permissions = useResourcePermissions(ResourceType.BOOKING);
    const superUser = useSuperUser();

    const upcoming =
        booking.eventStatus === EventStatuses.UPCOMING ||
        booking.eventStatus === EventStatuses.DRAFT;
    const allowAction = superUser ? true : upcoming;

    return (
        <>
            <Card
                flexGrow={2}
                sx={{
                    height: '192px',
                }}
                isSelected={isSelected}
                direction="row"
                justifyContent="space-evenly"
                buttons={
                    <>
                        {permissions.update && (
                            <IconButtonGroup>
                                <IconButton
                                    color="#37C16E"
                                    hoverColor="#164d2c"
                                    onClick={() => {
                                        if (status === BookingStatus.DRAFT) {
                                            setPayDraft(true);
                                        } else {
                                            setPayBooking(true);
                                        }
                                    }}
                                    // disabled={!financials.debt}
                                    icon={
                                        <PaymentsOutlinedIcon fontSize="small" />
                                    }
                                    tooltip="Payments"
                                />
                            </IconButtonGroup>
                        )}
                        {status !== BookingStatus.CANCELLED ? (
                            <IconButtonGroup>
                                <IconButton
                                    href={`/order/${orderId}/booking/${id}`}
                                    icon={<VisibilityIcon fontSize="small" />}
                                    tooltip="Preview"
                                />
                                {permissions.update &&
                                    allowAction &&
                                    status !== BookingStatus.CONFIRMED && (
                                        <>
                                            <Divider sx={{ mx: 0.75 }} />
                                            <IconButton
                                                onClick={() =>
                                                    setHoldBooking(true)
                                                }
                                                icon={
                                                    <CheckCircleIcon fontSize="small" />
                                                }
                                                color={
                                                    status ===
                                                    BookingStatus.HOLD
                                                        ? 'warning.main'
                                                        : undefined
                                                }
                                                hoverColor={
                                                    status ===
                                                    BookingStatus.HOLD
                                                        ? 'warning.dark'
                                                        : undefined
                                                }
                                                disabled={
                                                    status !==
                                                        BookingStatus.DRAFT &&
                                                    status !==
                                                        BookingStatus.HOLD
                                                }
                                                tooltip="Status change"
                                            />
                                        </>
                                    )}
                                {permissions.update && allowAction && (
                                    <>
                                        <Divider sx={{ mx: 0.75 }} />
                                        <IconButton
                                            onClick={() => {
                                                setMoveBooking(true);
                                            }}
                                            icon={
                                                <ExitToAppIcon fontSize="small" />
                                            }
                                            tooltip="Move"
                                        />
                                    </>
                                )}
                                {permissions.delete && allowAction && (
                                    <>
                                        <Divider sx={{ mx: 0.75 }} />
                                        <IconButton
                                            hoverColor="error.main"
                                            onClick={() =>
                                                setDeletingBooking(id)
                                            }
                                            icon={
                                                <DeleteIcon fontSize="small" />
                                            }
                                            tooltip="Cancel"
                                        />
                                    </>
                                )}
                            </IconButtonGroup>
                        ) : (
                            <IconButtonGroup>
                                <IconButton
                                    href={`/order/${orderId}/booking/${id}`}
                                    icon={<VisibilityIcon fontSize="small" />}
                                    tooltip="Preview"
                                />
                            </IconButtonGroup>
                        )}
                    </>
                }
            >
                <Stack
                    sx={{
                        width: '50%',
                        filter:
                            status === BookingStatus.CANCELLED
                                ? 'blur(2px)'
                                : undefined,
                    }}
                >
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                            px: 2,
                            height: '46px',
                            py: '10px',
                            bgcolor: '#F4F6FA',
                            borderRadius: '12px 0px 0px 0px',
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                color: '#6B748C',
                                pr: 1,
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {product.name}
                        </Box>
                        <Tag
                            value={`${
                                product.type === ProductType.TOUR
                                    ? shared
                                        ? 'Group'
                                        : 'Private'
                                    : ''
                            } ${pt(product.type)}`}
                        />
                    </Stack>
                    <Stack
                        sx={{
                            grow: 2,
                            pl: 2,
                            pr: 2 + 2,
                            py: '10px',
                            height: '144px',
                        }}
                        direction="row"
                        justifyContent="space-evenly"
                        gap={1}
                    >
                        <SquareCard
                            title="Start Date"
                            value={format(date.start, 'dd MMM yyyy')}
                            Icon={CalendarMonthIcon}
                        />
                        <SquareCard
                            title="Start Time"
                            value={format(date.start, 'HH:mm')}
                            Icon={AccessTimeIcon}
                        />
                        <SquareCard
                            title="Duration"
                            value={duration}
                            Icon={AccessTimeIcon}
                        />
                    </Stack>
                </Stack>
                <Stack
                    sx={{
                        width: '50%',
                        borderLeft: '2px solid #C9CEDC',
                        position: 'relative',
                        filter:
                            status === BookingStatus.CANCELLED
                                ? 'blur(2px)'
                                : undefined,
                    }}
                >
                    <Box
                        component={PerfectScrollbar}
                        sx={{
                            '&&': { height: '46px' },
                            top: 0,
                            left: 0,
                            right: 0,
                            zIndex: 1,
                        }}
                        option={{
                            suppressScrollY: true,
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="flex-end"
                            gap={2}
                            sx={{
                                px: 1,
                                height: '46px',
                                py: '10px',
                                bgcolor: '#F4F6FA',
                                borderRadius: '0px 12px 0px 0px',
                            }}
                        >
                            <Stack direction="row" gap={1}>
                                {sourceValue && (
                                    <Tag label="Source" value={sourceValue} />
                                )}
                                {financials.debt !== undefined && (
                                    <StatusTag
                                        status={
                                            financials.debt > 0
                                                ? StatusTagStatuses.WARNING
                                                : StatusTagStatuses.NEUTRAL
                                        }
                                        label="Debt"
                                        value={formatMoney(
                                            financials.debt,
                                            pricing.currency.abbr
                                        )}
                                    />
                                )}
                            </Stack>
                        </Stack>
                    </Box>
                    <Stack
                        sx={{
                            height: '144px',
                            overflow: 'hidden',
                            p: 2,
                            py: '10px',
                        }}
                    >
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ mb: 1 }}
                        >
                            <Box
                                sx={{
                                    color: '#6B748C',
                                    fontSize: '14px',
                                    fontWeight: '400',
                                }}
                            >
                                PAX
                            </Box>
                            <Box
                                sx={{
                                    color: '#2B395B',
                                    textAlign: 'right',
                                    fontSize: '14px',
                                    fontWeight: '400',
                                }}
                            >
                                {formatValue(participants.pax)}
                            </Box>
                        </Stack>
                        <Divider sx={{ mb: 1 }} />
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ mb: 1 }}
                        >
                            <Box
                                sx={{
                                    color: '#6B748C',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                }}
                            >
                                Total before discount
                            </Box>
                            <Box
                                sx={{
                                    color: '#6B758D',
                                    textAlign: 'right',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                }}
                            >
                                {formatMoney(
                                    financials.subtotal,
                                    pricing.currency.abbr
                                )}
                            </Box>
                        </Stack>
                        {!!pricing.discount?.amount && (
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                sx={{ mb: 1 }}
                            >
                                <Box
                                    sx={{
                                        color: '#6B748C',
                                        fontSize: '13px',
                                        fontWeight: '400',
                                    }}
                                >
                                    Discount
                                </Box>
                                <Box
                                    sx={{
                                        color: '#6B758D',
                                        textAlign: 'right',
                                        fontSize: '13px',
                                        fontWeight: '400',
                                    }}
                                >
                                    {discountFormatted}
                                </Box>
                            </Stack>
                        )}
                        <Divider />
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ mt: 1, mb: 1.5 }}
                        >
                            <Box
                                sx={{
                                    color: '#6B748C',
                                    fontSize: '15px',
                                    fontWeight: '500',
                                }}
                            >
                                {pricing.discount?.amount
                                    ? 'Total after discount'
                                    : 'Total'}
                            </Box>
                            <Box
                                sx={{
                                    color: '#2B395B',
                                    textAlign: 'right',
                                    fontSize: '15px',
                                    fontWeight: '500',
                                }}
                            >
                                {finalPrice}
                            </Box>
                        </Stack>
                    </Stack>
                </Stack>
                {status === BookingStatus.CANCELLED && (
                    <OverlayWithReason title="Cancelled" reason={reason} />
                )}
            </Card>
            <ReasonModal
                title="Are you sure you want to cancel this booking?"
                content="Provide the reason for cancelling this booking"
                confirmText="Confirm"
                open={!!deletingBooking}
                isLoading={isRemoveBookingLoading}
                handleCancel={() => setDeletingBooking(undefined)}
                handleConfirm={message => {
                    removeBooking({
                        bookingId: deletingBooking as string,
                        reason: message,
                    });
                }}
            />
            <HoldAvailabilityDialog
                open={holdBooking}
                isLoading={isHoldBookingLoading || isReserveLoading}
                handleCancel={() => setHoldBooking(false)}
                handleReserve={() => {
                    reserve({ bookingId: id as string });
                }}
                handleHold={() => {
                    holdAvailability({ bookingId: id as string });
                }}
                disableHold={status === BookingStatus.HOLD}
                disableReserve={!participants.travelers?.length}
            />
            <ConfirmationDialog
                title="Cannot accept a payment for a draft booking"
                content="Please select one of the following actions"
                confirmText="Hold Availability"
                open={payDraft}
                handleCancel={() => setPayDraft(false)}
                handleConfirm={() => {
                    holdAvailability(
                        { bookingId: id as string },
                        {
                            onSuccess: () => {
                                setPayDraft(false);
                                setPayBooking(true);
                            },
                        }
                    );
                }}
            />
            <TransactionModal
                currency={pricing.currency.abbr}
                title="Add a transaction"
                content=""
                isLoading={isAddTransactionLoading}
                confirmText="Add"
                open={payBooking}
                availableMethods={pricing.paymentRequirements.methods}
                handleCancel={() => setPayBooking(false)}
                handleConfirm={({ method, amount, type }) => {
                    addTransaction({
                        bookingId: id as string,
                        type,
                        method,
                        amount,
                    });
                }}
            />
            <MoveBookingModal
                handleCancel={() => setMoveBooking(false)}
                handleConfirm={() => {
                    refetch();
                    setMoveBooking(false);
                }}
                booking={booking}
                open={moveBooking}
            />
        </>
    );
};

export { BookingItemSkeleton };

export default React.memo(BookingItem);
